'use client';

import { useState } from 'react';

import { resetPassword } from '@/app/actions/reset-password';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { Mail } from 'lucide-react';
import posthog from 'posthog-js';

export function ResetPasswordForm() {
  const emailId = useState(() => `email-${Math.random()}`)[0];
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await resetPassword(email);
      posthog.capture('password_reset_requested', { auth_method: 'email' });
      setSuccess(true);
    } catch (err) {
      posthog.captureException(err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className='space-y-4'>
        <div className='rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20'>
          <div className='flex items-center'>
            <Mail className='mr-2 size-5 text-green-600 dark:text-green-400' />
            <p className='text-sm text-green-800 dark:text-green-200'>
              Password reset email sent! Check your inbox for instructions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {error && (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
          <p className='text-sm text-red-800 dark:text-red-200'>{error}</p>
        </div>
      )}

      <div className='text-center'>
        <p className='text-muted-foreground mb-4 text-sm'>
          Enter your email address and we'll send you a link to reset your
          password.
        </p>
      </div>

      <form
        onSubmit={handleReset}
        className='space-y-4'>
        <div className='space-y-2'>
          <label
            htmlFor={emailId}
            className='text-sm font-medium'>
            Email
          </label>
          <Input
            id={emailId}
            type='email'
            placeholder='name@example.com'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />
        </div>

        <Button
          type='submit'
          className='w-full'
          disabled={loading}>
          {loading ? 'Sending...' : 'Send Reset Link'}
        </Button>
      </form>
    </div>
  );
}
